import { useParams, Link, Navigate } from "react-router-dom";
import MarketingNav from "@/components/app/MarketingNav";
import { Button } from "@/components/ui/button";
import { Check, X, ArrowRight, Sparkles } from "lucide-react";

const DATA = {
  ahrefs: {
    name: "Ahrefs", price: "$129–$1,499/mo",
    summary: "Ahrefs is the gold standard for enterprise SEO teams with deep backlink indexes — but the price tag and dense, 2014-era UI make it overkill for indie founders, marketers, and small agencies.",
    rows: [
      ["AI action plan (prioritized roadmap)", true, false],
      ["AI article writer (full Markdown drafts)", true, false],
      ["Magic Mode (keyword → publish-ready draft)", true, false],
      ["SERP content gap analyzer", true, true],
      ["Free public audit (no signup)", true, false],
      ["Backlink index (real data)", false, true],
      ["Modern, Linear/Vercel-style UX", true, false],
      ["Starts free", true, false],
    ],
  },
  semrush: {
    name: "SEMrush", price: "$140–$500/mo",
    summary: "SEMrush is a Swiss-army knife covering SEO + PPC + social + content — but most users pay for 70+ features they never touch. ArloSEO picks the AI-first sliver that actually moves rankings.",
    rows: [
      ["AI action plan (prioritized roadmap)", true, false],
      ["AI article writer (full Markdown drafts)", true, "partial"],
      ["Magic Mode (keyword → publish-ready draft)", true, false],
      ["SERP content gap analyzer", true, true],
      ["Built-in editor with live Score Goal", true, false],
      ["Free public audit (no signup)", true, false],
      ["Honest, no-cap pricing", true, false],
      ["Starts free", true, false],
    ],
  },
  moz: {
    name: "Moz Pro", price: "$99–$600/mo",
    summary: "Moz coined Domain Authority and built a loyal SEO following — but innovation has slowed and the AI layer is bolt-on at best. ArloSEO is AI-native from the ground up.",
    rows: [
      ["AI action plan (prioritized roadmap)", true, false],
      ["AI article writer (full Markdown drafts)", true, false],
      ["Magic Mode (keyword → publish-ready draft)", true, false],
      ["Gamified Score Goal + Quest Log", true, false],
      ["SERP content gap analyzer", true, false],
      ["Domain Authority metric", true, true],
      ["Free public audit (no signup)", true, false],
      ["Starts free", true, false],
    ],
  },
};

function Cell({ v }) {
  if (v === true) return <Check className="w-4 h-4 text-emerald-600 mx-auto" />;
  if (v === "partial") return <span className="font-mono-data text-[10px] text-amber-700">partial</span>;
  return <X className="w-4 h-4 text-muted-foreground mx-auto" />;
}

export default function VsCompetitor() {
  const { competitor } = useParams();
  const data = DATA[competitor?.toLowerCase()];
  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <span className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// comparison</span>
          <h1 className="font-semibold tracking-tight mt-4 text-4xl md:text-6xl">
            ArloSEO <span className="text-muted-foreground">vs</span> {data.name}.
          </h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">{data.summary}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register">
              <Button className="rounded-none h-12 bg-foreground hover:bg-foreground/90 px-6" data-testid="vs-cta-register">
                Start free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/free-audit">
              <Button variant="outline" className="rounded-none h-12 px-6" data-testid="vs-cta-audit">
                <Sparkles className="w-4 h-4 mr-2" /> Try free audit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <h2 className="font-semibold tracking-tight text-3xl md:text-4xl">Feature comparison</h2>
          <div className="border border-border bg-white mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/40 font-mono-data text-[11px] uppercase tracking-widest">
                  <th className="text-left px-4 py-3">Feature</th>
                  <th className="text-center px-4 py-3 bg-foreground text-background">ArloSEO</th>
                  <th className="text-center px-4 py-3">{data.name}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(([label, a, b], i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">{label}</td>
                    <td className="px-4 py-3 text-center bg-secondary/30"><Cell v={a} /></td>
                    <td className="px-4 py-3 text-center"><Cell v={b} /></td>
                  </tr>
                ))}
                <tr className="font-mono-data">
                  <td className="px-4 py-3 text-muted-foreground uppercase tracking-widest text-[11px]">Pricing</td>
                  <td className="px-4 py-3 text-center bg-secondary/30 font-semibold">Free · $19 · $39 · $99</td>
                  <td className="px-4 py-3 text-center">{data.price}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-foreground text-background">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 flex flex-wrap items-center justify-between gap-6">
          <h3 className="font-semibold tracking-tight text-3xl">Stop paying for features you don&apos;t use.</h3>
          <Link to="/register">
            <Button className="rounded-none h-12 bg-background text-foreground hover:bg-background/90 px-7" data-testid="vs-cta-bottom">
              Switch to ArloSEO <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex items-center justify-between">
          <span className="font-semibold tracking-tight">ARLOSEO</span>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← Back to site</Link>
        </div>
      </footer>
    </div>
  );
}
